import { CommonPage, Content, LoadableContent, PageTitle, ResponsiveGap } from "common/Grid";
import { TableView } from "./TableView";
import { useGetSalesUsersListQuery } from "store/action/salesUserApi";
import { Form, FormField, PrimaryButton } from "common/Forms";
import { SelectInput, TextInput } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";
import { ScreenSizes, useScreenSizes } from "common/Grid/useScreenSize";
import { useNavigate, createSearchParams, useSearchParams } from "react-router-dom";
import { WithPagination } from "common/Pagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { roleOptions, userRoles } from "Constant/Constant";

const statusOptions = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

const List = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const screenSize = useScreenSizes();
  const columns = screenSize === ScreenSizes.Large ? 4 : screenSize === ScreenSizes.Medium ? 2 : 1;

  const searchFields = useMemo(
    () => ({
      page: { name: "p", default: 1 },
      limit: { name: "ps", default: 10 },
      search: { name: "search", default: "" },
      role: { name: "role", default: "" },
      isActive: { name: "isActive", default: "" },
    }),
    [],
  );

  const getSearchValue = useCallback(
    (name, defaultValue, getter = (value) => value) => {
      const value = searchParams.get(name);
      if (value === null) return defaultValue;
      return getter(value);
    },
    [searchParams],
  );

  const page = useRef(
    getSearchValue(
      searchFields.page.name,
      searchFields.page.default,
      (x) => parseInt(x) ?? searchFields.page.default,
    ),
  );
  const limit = useRef(
    getSearchValue(
      searchFields.limit.name,
      searchFields.limit.default,
      (x) => parseInt(x) ?? searchFields.limit.default,
    ),
  );

  const [sorting, setSorting] = useState({ sortBy: null, sortOrder: null });

  const onSortChange = useCallback((sortBy, sortOrder) => {
    setSorting(prev =>
      prev.sortBy === sortBy && prev.sortOrder === sortOrder
        ? { sortBy: null, sortOrder: null }
        : { sortBy, sortOrder }
    );
  }, []);

  const [filters, setFilters] = useState({
    search: getSearchValue(searchFields.search.name, ""),
    role: getSearchValue(searchFields.role.name, ""),
    isActive: getSearchValue(searchFields.isActive.name, ""),
  });

  const applySearch = useCallback(
    (overrideFilters) => {
      const activeFilters = overrideFilters ?? filters;
      const updatedSearchParams = createSearchParams(searchParams);
      updatedSearchParams.set(searchFields.page.name, `${page.current}`);
      updatedSearchParams.set(searchFields.limit.name, `${limit.current}`);

      if (activeFilters.search)
        updatedSearchParams.set(searchFields.search.name, activeFilters.search);
      else updatedSearchParams.delete(searchFields.search.name);

      if (activeFilters.role)
        updatedSearchParams.set(searchFields.role.name, activeFilters.role);
      else updatedSearchParams.delete(searchFields.role.name);

      if (activeFilters.isActive !== "")
        updatedSearchParams.set(searchFields.isActive.name, activeFilters.isActive);
      else updatedSearchParams.delete(searchFields.isActive.name);

      setSearchParams(updatedSearchParams);
    },
    [filters, searchFields, searchParams, setSearchParams],
  );

  const handleApplyFilters = () => {
    page.current = 1;
    applySearch();
  };

  const handleClearFilters = () => {
    const cleared = { search: "", role: "", isActive: "" };
    setFilters(cleared);
    page.current = 1;
    applySearch(cleared);
  };

  const onPageHandler = useCallback(
    (pageRequest) => {
      page.current = pageRequest + 1;
      applySearch();
    },
    [applySearch],
  );

  const onPageSizeHandler = useCallback(
    (sizeRequest) => {
      limit.current = sizeRequest;
      page.current = 1;
      applySearch();
    },
    [applySearch],
  );

  useEffect(() => {
    applySearch();
  }, []);

  const queryArgs = {
    page: getSearchValue(searchFields.page.name, searchFields.page.default, (x) => parseInt(x)),
    limit: getSearchValue(searchFields.limit.name, searchFields.limit.default, (x) => parseInt(x)),
    search: getSearchValue(searchFields.search.name, ""),
    role: getSearchValue(searchFields.role.name, "") || [userRoles.SALES_MANAGER, userRoles.SALES_PERSON],
    isActive: getSearchValue(searchFields.isActive.name, ""),
    ...(sorting.sortBy !== null && { sortBy: sorting.sortBy, sortOrder: sorting.sortOrder }),
  };

  const {
    data: response,
    isLoading,
    isError,
  } = useGetSalesUsersListQuery(queryArgs, { refetchOnMountOrArgChange: true });

  const allUsers = response?.data || [];

  return (
    <CommonPage>
      <Content>
        <PageTitle title="Sales Users">
          <PrimaryButton
            action={() => navigate("/create-sales-user")}
            classNames={["text-[16px]"]}>
            Create Sales User
          </PrimaryButton>
        </PageTitle>

        <ResponsiveGap left={false} right={false}>
          <Form>
            <Grid columns={columns} classNames={["!gap-x-3 !gap-y-5"]}>
              <GridCell>
                <FormField
                  id="search"
                  label="Search"
                  classNames={["text_input_box"]}
                  inputFactory={(id, name) => (
                    <TextInput
                      id={id}
                      name={name}
                      placeholder="Search by name, email..."
                      value={filters.search}
                      onChange={(_, val) => setFilters((prev) => ({ ...prev, search: val }))}
                    />
                  )}
                />
              </GridCell>

              <GridCell>
                <FormField
                  id="filterStatus"
                  label="Status"
                  inputFactory={(id, name) => (
                    <SelectInput
                      id={id}
                      name={name}
                      placeholder="Select status"
                      selectedKey={filters.isActive}
                      optionProvider={() => statusOptions}
                      keySelector={(x) => x.value}
                      labelSelector={(x) => x.label}
                      isClearable={true}
                      onChange={(_, n) =>
                        setFilters((prev) => ({ ...prev, isActive: n ? n.value : "" }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell>
                <FormField
                  id="filterRole"
                  label="Role"
                  inputFactory={(id, name) => (
                    <SelectInput
                      id={id}
                      name={name}
                      placeholder="Select role"
                      selectedKey={filters.role}
                      optionProvider={() => roleOptions}
                      keySelector={(x) => x.value}
                      labelSelector={(x) => x.label}
                      isClearable={true}
                      onChange={(_, n) =>
                        setFilters((prev) => ({ ...prev, role: n ? n.value : "" }))
                      }
                    />
                  )}
                />
              </GridCell>

              <GridCell vertical="bottom" classNames={["pb-1"]}>
                <Grid columns={2} columnGap={1}>
                  <GridCell>
                    <PrimaryButton action={handleApplyFilters} isWide={true} classNames={["justify-center"]}>
                      Apply Filters
                    </PrimaryButton>
                  </GridCell>
                  <GridCell>
                    <PrimaryButton action={handleClearFilters} isWide={true} classNames={["justify-center"]}>
                      Clear Filters
                    </PrimaryButton>
                  </GridCell>
                </Grid>
              </GridCell>
            </Grid>
          </Form>
        </ResponsiveGap>

        <LoadableContent isLoading={isLoading} isError={isError}>
          <WithPagination
            id="pagedData"
            currentPage={(response?.currentPage ?? 1) - 1}
            totalPages={response?.totalPages ?? 0}
            totalItems={response?.total ?? 0}
            pageSize={limit.current ?? 0}
            onPageChange={onPageHandler}
            onPageSizeChange={onPageSizeHandler}
            paginationWrapper={(x) => <ResponsiveGap>{x}</ResponsiveGap>}>
            <TableView salesUserList={allUsers} sorting={sorting} onSortChange={onSortChange} />
          </WithPagination>
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default List;
